import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { PlatformConfig, PlatformProvider, ViewportProvider } from '@tofu/platform';
import { DiscordEventsProvider, FanforgeAuthProvider } from '@hotpot-arcade/fanforge-auth';
import { FanforgeApiProvider } from '@hotpot-arcade/fanforge-api-client';
import './app/app.css';
import { App } from './app/app';
import { AnalyticsProvider } from '@hotpot-arcade/analytics-client';
import { FanforgeConfigProvider, FanforgeConfig } from '@hotpot-arcade/fanforge-core-client';
import * as Sentry from '@sentry/react';
import { sentryTunnelFromDSN } from './utils/sentry-utils';

const queryClient = new QueryClient({});
console.log('STARTING APP');

const platformConfig: PlatformConfig = {
  // ** discord **
  discordClientId: import.meta.env.VITE_DISCORD_CLIENT_ID,
  discordProxyUrl: import.meta.env.VITE_DISCORD_PROXY_DOMAIN,
  discordPatchUrlMappings: [
    { prefix: '/platform', target: import.meta.env.VITE_PLATFORM_API.replace(/^https?:\/\//, '') },
    { prefix: '/mafia-ws', target: import.meta.env.VITE_MAFIA_WEBSOCKET.replace(/^https?:\/\//, '') },
    { prefix: '/posthog', target: import.meta.env.VITE_PUBLIC_POSTHOG_HOST.replace(/^https?:\/\//, '') },
    {
      prefix: '/sentry',
      target: import.meta.env.VITE_SENTRY_DSN.replace(/^https?:\/\/[^@]+@/, '').replace(/\/.*$/, ''),
    },
    { prefix: '/media', target: import.meta.env.VITE_FANFORGE_ASSETS_URL.replace(/^https?:\/\//, '') },
  ],
};

function getMode() {
  switch (import.meta.env.MODE) {
    case 'development':
      return 'development';
    case 'staging':
      return 'staging';
    case 'production':
      return 'production';
    default:
      return 'unknown';
  }
}

const fanforgeConfig: FanforgeConfig = {
  // *** misc config ***
  allowDeviceLogin: true, //import.meta.env.MODE === 'development',

  // *** platform api config ***
  platformApiUrl: import.meta.env.VITE_PLATFORM_API,

  // *** analytics config ***
  postHogHost: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  postHogKey: import.meta.env.VITE_PUBLIC_POSTHOG_KEY,

  // ** fanforge-meta config **
  assetsUrl: import.meta.env.VITE_FANFORGE_ASSETS_URL,

  // ** misc **
  mode: getMode(),
};

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  tunnel: sentryTunnelFromDSN(import.meta.env.VITE_SENTRY_DSN),
  enabled: import.meta.env.MODE !== 'development',
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.captureConsoleIntegration({
      levels: ['error'],
    }),
    Sentry.browserTracingIntegration(),
  ],
  tracesSampleRate: 1.0,
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <PlatformProvider platformConfig={platformConfig}>
        <FanforgeConfigProvider config={fanforgeConfig}>
          <FanforgeApiProvider>
            <FanforgeAuthProvider>
              <DiscordEventsProvider>
                <AnalyticsProvider>
                  <ViewportProvider>
                    <App />
                  </ViewportProvider>
                </AnalyticsProvider>
              </DiscordEventsProvider>
            </FanforgeAuthProvider>
          </FanforgeApiProvider>
        </FanforgeConfigProvider>
      </PlatformProvider>
    </QueryClientProvider>
  </StrictMode>
);
